import React, { useState, useCallback } from "react";
import TableTemplate from "../../components/common/TableTemplate";
import { columnHelper } from "../../utils/utils";
import { useGetMarketProductsQuery } from "../../services/market";
import { generateDynamicColumns } from "../../utils/generateDynamicColumns";
import LoadingState from "../../components/states/LoadingState";
import ErrorState from "../../components/states/ErrorState";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { schemas, validateAndToast } from "../../utils/validation";
import ChartViewTemplate from "../../components/charts/ChartViewTemplate";
import {
  transformGlobalChartData,
  getGlobalChartTitle,
  hasGlobalChartData,
} from "../../utils/marketLabChartUtils";

const MarketlabsProductsPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchBy, setSearchBy] = useState("");
  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState("");

  const {
    data: marketProducts,
    isLoading,
    isError,
    error,
    isFetching,
  } = useGetMarketProductsQuery({
    pageNumber: currentPage,
    pageSize: pageSize,
    search_query: searchBy,
    filters: filters,
    sort_by: sortBy,
  });

  // console.log("marketProducts", marketProducts);
  const selectedRows = useSelector((state) => state.table.selectedRows);

  // Extract pagination info from API response
  const paginationInfo = marketProducts?.[0]
    ? {
        totalPages: marketProducts[0].total_page || 0,
        totalItems: marketProducts[0].total_items || 0,
        currentPage: marketProducts[0].current_page || 1,
        isPaginated: marketProducts[0].type === "paginated_dgv",
      }
    : null;

  // Server-side search handler
  const handleServerSearch = useCallback((searchValue) => {
    setSearchBy(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  // Server-side filter handler
  const handleServerFilter = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  // Server-side sorting handler
  const handleServerSort = useCallback(
    (sortingState) => {
      console.log("🎯 HANDLE SERVER SORT CALLED:", {
        sortingState: sortingState,
        previousSortBy: sortBy,
      });

      // Convert TanStack Table sorting state to backend format
      if (sortingState.length > 0) {
        const { id, desc } = sortingState[0];
        console.log("🔍 DETAILED SORT INFO:", {
          id: id,
          desc: desc,
          type_of_desc: typeof desc,
          sortingState_full: sortingState[0],
        });
        // Format: field_name for ascending, -field_name for descending
        const sortValue = desc ? `-${id}` : id;
        console.log("📤 SETTING SORT VALUE:", sortValue);

        // Only set if different from current value
        if (sortValue !== sortBy) {
          setSortBy(sortValue);
        } else {
          console.log("⚠️ SORT VALUE UNCHANGED, NOT SETTING");
        }
      } else {
        console.log("🔄 CLEARING SORT VALUE");
        setSortBy(""); // Clear sorting
      }
      setCurrentPage(1); // Reset to first page when sorting
    },
    [sortBy]
  );

  const columns = React.useMemo(() => {
    if (!marketProducts?.[0]?.Column_header) {
      console.log("No column header found, returning empty columns");
      return [];
    }

    return generateDynamicColumns({
      columnHelper,
      headerData: marketProducts[0].Column_header,
      navigationConfig: {
        // Make product name navigable to details page
        name: {
          navigable: true,
          target: "_self", // Navigate within app
          getUrl: ({ originalRow, columnInfo }) => {
            const productId = originalRow?.glid?.display;
            return productId
              ? {
                  pathname: `/market-product/${productId}`,
                  state: null,
                }
              : null;
          },
        },
        // Only make uri type fields navigable (dest_url has type: "uri")
        dest_url: {
          navigable: true,
          target: "_blank", // Open in new tab
          getUrl: ({ originalRow, columnInfo }) => {
            const url =
              originalRow?.[columnInfo.key]?.display ||
              originalRow?.[columnInfo.key]?.url;
            return url
              ? {
                  pathname: url,
                  state: null,
                }
              : null;
          },
        },
      },
      filterConfig: {
        filterTypes: {
          status: "select",
        },
        excludeFromFilter: ["actions", "rowSelection"],
      },
      editableFields: {
        // Only allow editing for specific fields (exclude ID fields like glid)
        name: {
          onSave: async (columnInfo, row, editedValue) => {
            toast.error(
              "Editing is not available for market products yet. Update service needs to be implemented."
            );
            return false;
          },
          placeholder: "Enter product name",
        },
        dest_url: {
          inputType: "url",
          placeholder: "Enter destination URL",
          onSave: async (columnInfo, row, editedValue) => {
            // Validate URL format
            if (!(await validateAndToast(editedValue, schemas.url))) {
              return false;
            }
            toast.error(
              "Editing is not available for market products yet. Update service needs to be implemented."
            );
            return false;
          },
        },
      },
    });
  }, [marketProducts, selectedRows]);

  const tableOptions = {
    enableGlobalFilter: true, // Enable global filter for server-side search
    enablePagination: paginationInfo?.isPaginated || false, // Enable pagination based on API response
    enableColumnVisibility: true, // Enable column visibility controls
    enableSorting: true,
    enableColumnFilters: true, // Enable column filters for server-side filtering
    enableRowSelection: false, // Disable row selection temporarily to test column resizing
    enableColumnResize: true, // Enable column resizing
    enableColumnOrder: true, // Enable column ordering
    defaultPageSize: pageSize,
    showTotalItems: true, // Show total items for large datasets
    showPreferences: true, // Enable preferences panel for column controls
    // Server-side pagination configuration
    manualPagination: paginationInfo?.isPaginated || false,
    pageCount: paginationInfo?.totalPages || 0,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newPagination = updater({ pageIndex: currentPage - 1, pageSize });
        setCurrentPage(newPagination.pageIndex + 1);
        setPageSize(newPagination.pageSize);
      } else {
        setCurrentPage(updater.pageIndex + 1);
        setPageSize(updater.pageSize);
      }
    },
    state: {
      pagination: {
        pageIndex: currentPage - 1, // TanStack Table uses 0-based indexing
        pageSize: pageSize,
      },
    },
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full">
        <LoadingState />
      </div>
    );
  }

  // Show error state
  if (isError) {
    console.error("Market products query error:", error);
    return <ErrorState error={error} />;
  }

  // Show message if no data
  if (!marketProducts || marketProducts.length === 0) {
    return (
      <div className="w-full">
        <div className="p-4 text-center">
          <h2 className="text-xl font-semibold text-gray-600">
            No Data Available
          </h2>
          <p className="text-gray-500 mt-2">No market products data found.</p>
        </div>
      </div>
    );
  }

  // Show message if data structure is unexpected
  if (!marketProducts[0]?.data) {
    console.warn("Unexpected data structure:", marketProducts[0]);
    return (
      <div className="w-full">
        <div className="p-4">
          <h2 className="text-xl font-semibold text-gray-600">
            Data Structure Issue
          </h2>
          <p className="text-gray-500 mt-2">
            The data structure is not as expected. Check console for details.
          </p>
          <pre className="mt-4 p-4 bg-gray-100 rounded text-sm overflow-auto">
            {JSON.stringify(marketProducts[0], null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="p-4 rounded-lg ">
        <h1 className="text-2xl font-bold text-gray-800">Products</h1>
        <div className="text-sm text-gray-600 mt-1">
          {paginationInfo?.isPaginated ? (
            <>
              Total records: {paginationInfo.totalItems || 0} | Page{" "}
              {paginationInfo.currentPage || 1} of{" "}
              {paginationInfo.totalPages || 0} | Showing{" "}
              {marketProducts[0]?.data?.length || 0} items per page
            </>
          ) : (
            <>
              Total records: {marketProducts[0]?.data?.length || 0} | Data size:
              ~{Math.round(JSON.stringify(marketProducts).length / 1024 / 1024)}
              MB
            </>
          )}
        </div>
      </div>

      {/* Global Chart */}
      {hasGlobalChartData(marketProducts) && (
        <div className="bg-white rounded-lg border-gray-200 shadow-sm">
          <ChartViewTemplate
            title={getGlobalChartTitle(marketProducts)}
            description="Global market data trends"
            data={transformGlobalChartData(marketProducts)}
            footerText="Showing overall market performance across all products"
            hidePieChart={true}
          />
        </div>
      )}

      {/* Table Container */}
      <div className="bg-white rounded-lg border-gray-200 shadow-sm w-full">
        <style>{`
          .market-products-table table {
            width: 100% !important;
          }
          .market-products-table th,
          .market-products-table td {
            min-width: 120px;
          }
        `}</style>
        <div className="market-products-table">
          <TableTemplate
            columns={columns}
            data={marketProducts[0].data}
            options={tableOptions}
            isLoading={isFetching}
            layout="standard"
            onServerSearch={handleServerSearch}
            serverSearchValue={searchBy}
            onServerFilter={handleServerFilter}
            serverFilters={filters}
            onServerSort={handleServerSort}
            serverSortBy={sortBy}
            isPaginated={paginationInfo?.isPaginated}
          />
        </div>
      </div>
    </div>
  );
};

export default MarketlabsProductsPage;
